/**
 * Hands you can see, and hands you can read.
 *
 * Two things live here because they are both "the player's hands" and neither
 * is big enough to be its own file: the visible controller decoration that a
 * session hangs in the scene, and the articulated-joint reader that turns a
 * tracked hand into a pinch.
 *
 * ## The visible hand
 *
 * Two coordinate spaces per controller, both of them three's:
 *
 *   grip space   where the hand is. The visible blob hangs here.
 *   ray space    where the hand is *pointing*. The pointer and the torch hang
 *                here, because this is the only one the WebXR spec pins down
 *                exactly — a target ray always runs along -Z — and a torch
 *                that shines somewhere other than where you are pointing it is
 *                worse than no torch at all.
 *
 * No models are loaded. `three/examples/jsm/webxr/XRControllerModelFactory`
 * would fetch a glTF per controller off a CDN, which is both a binary asset
 * and a network dependency, and this game has neither. A blob and an outline
 * read as a hand from the inside of a headset and cost two draw calls.
 *
 * Everything is `MeshBasicMaterial` on purpose: the hands must be visible in a
 * corridor with every tube dead, and a lit material in this game's darkness is
 * a hand you cannot find.
 *
 * ## The read hand
 *
 * `XRHandPinch` at the bottom of the file is the third of the three input
 * producers behind `xrintent.ts` — the one for a headset where the player has
 * put the controllers down, or never had any. It has no buttons to poll, so a
 * press has to be measured: thumb tip to index tip, in metres, with hysteresis
 * so a hand held still at the threshold does not chatter a press on and off at
 * the frame rate. See the class for why the threshold scales with the player's
 * own fingertips rather than being a fixed number.
 */

import * as THREE from 'three';
import { clamp } from '../core/math';

/** Metres. A fist, roughly, and small enough not to sit in front of the eyes. */
const BLOB_RADIUS = 0.036;

/** How much larger the backface shell is. This is the whole outline trick. */
const OUTLINE_SCALE = 1.16;

/** Length of the pointer stub in metres. Short: a laser in a horror game is a toy. */
const RAY_LENGTH = 0.34;

/**
 * Where the torch mounts on the ray space.
 *
 * `render/flashlight.ts` builds its beam already offset for a head mount —
 * 16 cm right of and 10 cm below the eye, so the cone edge crawls over
 * doorframes as the player turns. On a hand that offset is simply wrong, and
 * flashlight.ts is not this file's to change, so the mount cancels it and the
 * beam comes out of the hand.
 */
const TORCH_MOUNT = new THREE.Vector3(-0.16, 0.1, 0);

/** One controller, as the player sees it. */
export interface XRHandView {
  /** three's target-ray space. Add to the rig; the torch mount lives on it. */
  readonly ray: THREE.Group;
  /** three's grip space. Add to the rig; the visible hand lives on it. */
  readonly grip: THREE.Group;
  /**
   * Where a held object is parented so that its own -Z ends up along the
   * pointing ray. The flashlight goes here.
   */
  readonly mount: THREE.Object3D;
  /** Show or hide the pointer stub. */
  setPointer(on: boolean): void;
  /** Show a torch body in this hand. Only true once the player owns one. */
  setHolding(on: boolean): void;
  dispose(): void;
}

/**
 * Build the visuals for one controller index. The groups come from three and
 * are driven by the session; this only decorates them.
 */
export function makeHandView(renderer: THREE.WebGLRenderer, index: number): XRHandView {
  const ray = renderer.xr.getController(index);
  const grip = renderer.xr.getControllerGrip(index);

  const owned: Array<{ dispose(): void }> = [];
  const track = <T extends { dispose(): void }>(v: T): T => {
    owned.push(v);
    return v;
  };

  /* -- The hand itself, on the grip space -- */

  const blobGeom = track(new THREE.SphereGeometry(BLOB_RADIUS, 12, 8));
  const blobMat = track(new THREE.MeshBasicMaterial({ color: 0x24252a, fog: false, toneMapped: false }));
  const blob = new THREE.Mesh(blobGeom, blobMat);
  // Flattened across and stretched through, so it reads as a closed hand rather
  // than a ball whichever way round the runtime's grip axes turn out to be.
  blob.scale.set(0.92, 0.8, 1.3);

  const outlineMat = track(
    new THREE.MeshBasicMaterial({ color: 0x8d8f99, side: THREE.BackSide, fog: false, toneMapped: false })
  );
  const outline = new THREE.Mesh(blobGeom, outlineMat);
  outline.scale.copy(blob.scale).multiplyScalar(OUTLINE_SCALE);

  grip.add(blob, outline);

  /* -- The pointer, on the ray space -- */

  const rayGeom = track(new THREE.CylinderGeometry(0.0018, 0.0018, RAY_LENGTH, 6, 1, true));
  const rayMat = track(
    new THREE.MeshBasicMaterial({
      color: 0xffe4b8,
      transparent: true,
      opacity: 0.42,
      fog: false,
      toneMapped: false,
      depthWrite: false,
    })
  );
  const pointer = new THREE.Mesh(rayGeom, rayMat);
  // The cylinder is built along +Y; lay it down the -Z the spec guarantees.
  pointer.rotation.x = -Math.PI / 2;
  pointer.position.z = -RAY_LENGTH / 2;
  ray.add(pointer);

  /* -- The torch body, on the ray space, only once one has been found -- */

  const barrelGeom = track(new THREE.CylinderGeometry(0.019, 0.023, 0.13, 10));
  const barrelMat = track(new THREE.MeshBasicMaterial({ color: 0x3c3d42, fog: false, toneMapped: false }));
  const barrel = new THREE.Mesh(barrelGeom, barrelMat);
  barrel.rotation.x = -Math.PI / 2;
  barrel.position.set(0, -0.005, -0.05);
  barrel.visible = false;
  ray.add(barrel);

  const mount = new THREE.Object3D();
  mount.position.copy(TORCH_MOUNT);
  ray.add(mount);

  return {
    ray,
    grip,
    mount,

    setPointer(on: boolean): void {
      pointer.visible = on;
    },

    setHolding(on: boolean): void {
      barrel.visible = on;
    },

    dispose(): void {
      grip.remove(blob, outline);
      ray.remove(pointer, barrel, mount);
      for (const o of owned) o.dispose();
    },
  };
}

/* ------------------------------------------------------------------ *
 * Hand tracking: a pinch, measured
 * ------------------------------------------------------------------ */

/**
 * The joints a pinch is measured between, in the order they are packed into
 * `matrices`, `radii` and `pos`. Three of the twenty-five: reading the whole
 * skeleton to answer one boolean would cost twenty-two poses a hand a frame.
 */
const PINCH_JOINTS: readonly XRHandJoint[] = ['thumb-tip', 'index-finger-tip', 'middle-finger-tip'];

/**
 * Where the pinch thresholds come from.
 *
 * The distance between two fingertip *joints* when the fingers are actually
 * touching is not zero — it is roughly the sum of the two tips' radii, because
 * the joint sits at the centre of the fingertip. So the threshold is expressed
 * in those radii rather than in metres, which makes it the same gesture on an
 * adult's hands and on an eleven-year-old's; a fixed 2 cm is a firm pinch for
 * one and a hand already closed for the other, and this game is played by both.
 *
 * The runtime is allowed to report no radius at all, so there is a flat
 * fallback, and the derived numbers are clamped into a band that stays a pinch
 * whatever a runtime decides a fingertip is.
 */
const CLOSE_RADII = 1.45;
const OPEN_RADII = 2.4;
const CLOSE_MIN = 0.016;
const CLOSE_MAX = 0.032;
const CLOSE_FLAT = 0.024;
const OPEN_MIN = 0.028;
const OPEN_MAX = 0.05;
const OPEN_FLAT = 0.038;

/**
 * The bulk pose API, which `@types/webxr` does not carry.
 *
 * `XRFrame.fillPoses` writes n 4x4 matrices into a `Float32Array` the caller
 * owns, where `getJointPose` allocates an `XRJointPose` per joint per frame.
 * Three joints per hand, two hands, ninety frames a second is sixteen thousand
 * short-lived objects a second on a mobile GPU whose garbage collector shows up
 * as a dropped frame in both eyes. It is used when it exists and the per-joint
 * path is kept for when it does not.
 */
interface XRFrameBulkPoses {
  fillPoses?(spaces: readonly XRSpace[], baseSpace: XRSpace, transforms: Float32Array): boolean;
  fillJointRadii?(jointSpaces: readonly XRJointSpace[], radii: Float32Array): boolean;
}

/**
 * One tracked hand's pinch.
 *
 * Lives for the whole session on the input slot that owns it and is `bind`ed
 * and `unbind`ed as sources come and go, so no allocation happens when a hand
 * appears. Everything it reads goes into arrays it allocated once.
 *
 * Distances are computed in the reference space the poses were asked for, which
 * is safe for exactly one reason: the rig transform between that space and the
 * world is rigid, and the distance between two points does not change under a
 * rotation and a translation. So there is no need to push six joint positions
 * out to world space to ask whether two of them are touching.
 */
export class XRHandPinch {
  /** True while thumb and index are pinched. The primary action. */
  primary = false;
  /** True while thumb and *middle* are pinched and the index is not. */
  secondary = false;

  /** The three joint spaces, refilled in place on `bind`. */
  private readonly spaces: XRJointSpace[] = [];
  /** Row-major 4x4 per joint, as `fillPoses` writes them. */
  private readonly matrices = new Float32Array(PINCH_JOINTS.length * 16);
  private readonly radii = new Float32Array(PINCH_JOINTS.length);
  /** x,y,z per joint, in reference space. */
  private readonly pos = new Float32Array(PINCH_JOINTS.length * 3);
  /** False once this runtime has been found not to implement `fillPoses`. */
  private bulk = true;
  /**
   * Last frame's raw thumb-to-middle state, which is what the hysteresis has to
   * be measured against. `secondary` is that *and* the index being out, so
   * feeding `secondary` back in would reset the hysteresis every time the index
   * moved and make the middle pinch chatter for a reason that has nothing to do
   * with the middle finger.
   */
  private middleClosed = false;

  /**
   * Take the joints of a hand. Returns false when the runtime has an `XRHand`
   * that is missing one of the three joints, which is not a case the spec
   * allows but is cheaper to survive than to trust.
   */
  bind(hand: XRHand): boolean {
    this.spaces.length = 0;
    for (const name of PINCH_JOINTS) {
      const joint = hand.get(name);
      if (!joint) {
        this.spaces.length = 0;
        return false;
      }
      this.spaces.push(joint);
    }
    this.primary = false;
    this.secondary = false;
    this.middleClosed = false;
    this.radii.fill(0);
    return true;
  }

  /** Let go of the joints, and of the pinch they were describing. */
  unbind(): void {
    this.spaces.length = 0;
    this.primary = false;
    this.secondary = false;
    this.middleClosed = false;
  }

  /**
   * Read this frame's joints and update `primary` and `secondary`.
   *
   * Returns false when the joints could not be read — a hand behind the
   * player's back, out of the tracking volume, or momentarily occluded by the
   * other one. **A false is not a release.** Hand tracking drops poses
   * constantly and treating every dropout as the player letting go would fire a
   * teleport every time they turned their wrist; the caller holds the previous
   * state for a short grace period and only then gives up. `primary` and
   * `secondary` are deliberately left exactly as they were.
   */
  read(frame: XRFrame, refSpace: XRSpace): boolean {
    if (this.spaces.length !== PINCH_JOINTS.length) return false;
    if (!this.fill(frame, refSpace)) return false;

    const rThumb = this.radii[0];
    const dIndex = this.gap(0, 1);
    const dMiddle = this.gap(0, 2);
    const index = pinched(this.primary, dIndex, rThumb + this.radii[1]);
    const middle = pinched(this.middleClosed, dMiddle, rThumb + this.radii[2]);

    this.primary = index;
    this.middleClosed = middle;
    // Middle finger to thumb with the index left out: a gesture that is
    // deliberately *not* a superset of the primary one, so a player asking for
    // the secondary action can never also commit the primary. A two-finger
    // pinch would be easier to perform and impossible to disambiguate — the
    // index always arrives first, so it would fire a teleport on the way.
    this.secondary = middle && !index;
    return true;
  }

  /** Distance in metres between two of the three joints, in reference space. */
  private gap(a: number, b: number): number {
    const dx = this.pos[a * 3] - this.pos[b * 3];
    const dy = this.pos[a * 3 + 1] - this.pos[b * 3 + 1];
    const dz = this.pos[a * 3 + 2] - this.pos[b * 3 + 2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  /** Positions and radii into `pos` and `radii`. False when nothing is tracked. */
  private fill(frame: XRFrame, refSpace: XRSpace): boolean {
    const bulk = frame as XRFrame & XRFrameBulkPoses;
    if (this.bulk) {
      if (typeof bulk.fillPoses !== 'function') {
        this.bulk = false;
      } else if (bulk.fillPoses(this.spaces, refSpace, this.matrices) === true) {
        // Radii are the player's fingertips and do not change during a session,
        // but they are free here and a runtime is allowed to refine them.
        if (typeof bulk.fillJointRadii === 'function') bulk.fillJointRadii(this.spaces, this.radii);
        for (let i = 0; i < PINCH_JOINTS.length; i++) {
          // Column-major: the translation is elements 12, 13, 14.
          this.pos[i * 3] = this.matrices[i * 16 + 12];
          this.pos[i * 3 + 1] = this.matrices[i * 16 + 13];
          this.pos[i * 3 + 2] = this.matrices[i * 16 + 14];
        }
        return true;
      } else {
        // A false here is an untracked hand as often as an unimplemented API,
        // so `bulk` is deliberately not cleared: the next frame tries again.
        return false;
      }
    }
    const get = frame.getJointPose;
    if (typeof get !== 'function') return false;
    for (let i = 0; i < PINCH_JOINTS.length; i++) {
      const pose = get.call(frame, this.spaces[i]!, refSpace);
      if (!pose) return false;
      const p = pose.transform.position;
      this.pos[i * 3] = p.x;
      this.pos[i * 3 + 1] = p.y;
      this.pos[i * 3 + 2] = p.z;
      this.radii[i] = pose.radius ?? 0;
    }
    return true;
  }
}

/**
 * Hysteresis. Once closed a pinch stays closed until it opens noticeably wider
 * than it had to close, so a hand resting at the threshold does not flicker the
 * primary action on and off at ninety hertz — which downstream would be ninety
 * teleports a second.
 */
function pinched(was: boolean, gap: number, radiiSum: number): boolean {
  if (was) {
    return gap < (radiiSum > 0 ? clamp(radiiSum * OPEN_RADII, OPEN_MIN, OPEN_MAX) : OPEN_FLAT);
  }
  return gap < (radiiSum > 0 ? clamp(radiiSum * CLOSE_RADII, CLOSE_MIN, CLOSE_MAX) : CLOSE_FLAT);
}
